import { Box, IconButton, Skeleton, Stack, Typography } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import { useMemo, useRef, useState, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from 'shared/dates';
import {
  getDenseResponsiveFontSize,
  getResponsiveFontSize,
} from 'theme/watchdutyTheme.utils';
import { WatchDutyTimelineSlider } from 'components/WatchDutyTimelineSlider';

export type TimeFrame = {
  timestamp: Date;
  label: string;
};

type TimeMark = { value: number };

type TimeMarkLabel = { percent: number; hours: number };

type MarksInfo = {
  nowMs: number;
  nowPercent: number | null;
  timeMarks: TimeMark[];
  markLabels: TimeMarkLabel[];
};

export type AnimatedTimelineControlProps = {
  isLoading: boolean;
  isPlaying: boolean;
  frames: TimeFrame[];
  currentFrameIndex: number;
  onFrameChange: (index: number) => void;
  onPlayPause: () => void;
  markIntervalMinutes: number;
  /** Minutes between labeled marks, counted from now (e.g. `-24h`) */
  markLabelIntervalMinutes?: number;
  /** Bold, uppercase end label (e.g. `NOW`) */
  emphasizeEndLabel?: boolean;
  labels: {
    start: string;
    end: string;
  };
};

const PLAY_BUTTON_SIZE = 52;
const CURRENT_TIME_HEIGHT = 20;
const SLIDER_HEIGHT = 35;
const TRACK_HEIGHT = 8;
const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;

const styles = {
  timeLabelStyles: {
    opacity: 0.6,
    fontSize: getResponsiveFontSize('0.65rem'),
  },
  emphasizedTimeLabelStyles: {
    fontSize: getResponsiveFontSize('0.65rem'),
    fontWeight: 600,
    color: 'text.primary',
  },
} as const;

/**
 * Generate marks at `intervalMs` steps outward from `nowMs`, inside the
 * (startMs, endMs) range. Skips marks within a minute of either end, where
 * they'd overlap the start/end labels.
 */
const generateTimeMarks = (
  startMs: number,
  endMs: number,
  nowMs: number,
  intervalMs: number,
): TimeMark[] => {
  if (intervalMs <= 0) return [];

  const minMs = startMs + MINUTE_MS;
  const maxMs = endMs - MINUTE_MS;
  const result: TimeMark[] = [];
  for (let markMs = nowMs - intervalMs; markMs > minMs; markMs -= intervalMs) {
    if (markMs < maxMs) result.unshift({ value: markMs });
  }
  for (let markMs = nowMs + intervalMs; markMs < maxMs; markMs += intervalMs) {
    if (markMs > minMs) result.push({ value: markMs });
  }

  return result;
};

/**
 * Find the closest frame index to a given epoch ms timestamp.
 */
const findClosestFrameIndex = (frames: TimeFrame[], timeMs: number): number => {
  let closestIdx = 0;
  let closestDiff = Infinity;
  for (let i = 0; i < frames.length; i += 1) {
    const diff = Math.abs(frames[i].timestamp.getTime() - timeMs);
    if (diff < closestDiff) {
      closestDiff = diff;
      closestIdx = i;
    }
  }
  return closestIdx;
};

const SliderNowMarker = ({
  percent,
  isPastNow,
}: {
  percent: number;
  isPastNow: boolean;
}): JSX.Element => {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        position: 'absolute',
        // Above the track/rail (unset zIndex), below the thumb (zIndex: 2).
        zIndex: 1,
        left: `${percent}%`,
        transform: 'translateX(-50%)',
        // Center vertically on the 8px track within the 35px slider
        top: '20.5px',
        marginTop: '-12px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        pointerEvents: 'none',
      }}
    >
      <Box
        sx={(theme) => ({
          width: 14,
          height: 14,
          mt: 0.25,
          backgroundColor: theme.palette.background.default,
          backgroundClip: 'padding-box',
          border: `4px solid ${isPastNow ? theme.palette.brand[500] : theme.palette.warmGrey[500]}`,
          borderRadius: '50%',
        })}
      />
      <Typography
        sx={{
          fontSize: getDenseResponsiveFontSize('0.6rem'),
          fontWeight: 600,
          lineHeight: 1,
          textTransform: 'uppercase',
          mt: 1.5,
        }}
      >
        {t('map.timeline.now')}
      </Typography>
    </Box>
  );
};

export const AnimatedTimelineControl = (
  props: AnimatedTimelineControlProps,
): JSX.Element | null => {
  const {
    frames,
    currentFrameIndex,
    isLoading,
    isPlaying,
    onFrameChange,
    onPlayPause,
    markIntervalMinutes,
    markLabelIntervalMinutes,
    emphasizeEndLabel = false,
    labels,
  } = props;
  const { t } = useTranslation();

  const labelRef = useRef<HTMLSpanElement>(null);
  const [labelWidth, setLabelWidth] = useState(0);
  const prevIndexRef = useRef(currentFrameIndex);

  // Detect wrap (jumped backwards significantly = looped from end to start)
  const isWrapping = currentFrameIndex < prevIndexRef.current - 1;
  prevIndexRef.current = currentFrameIndex;

  // Re-measure when the label mounts after loading, not only on frame changes
  useLayoutEffect(() => {
    if (labelRef.current) {
      setLabelWidth(labelRef.current.offsetWidth);
    }
  }, [currentFrameIndex, isLoading, frames.length]);

  // Compute frame-dependent values only when not loading and frames exist
  const hasFrames = frames && frames.length > 0;
  const firstFrame = hasFrames ? frames[0] : null;
  const lastFrame = hasFrames ? frames[frames.length - 1] : null;
  const clampedIndex = hasFrames
    ? Math.max(0, Math.min(currentFrameIndex, frames.length - 1))
    : 0;
  const currentFrame = hasFrames ? frames[clampedIndex] : null;
  const labelOffset = labelWidth / 2;

  // Time-based slider values
  const startMs = firstFrame ? firstFrame.timestamp.getTime() : 0;
  const endMs = lastFrame ? lastFrame.timestamp.getTime() : 0;
  const currentMs = currentFrame ? currentFrame.timestamp.getTime() : 0;
  const timeRange = endMs - startMs || 1;
  const currentPercent = hasFrames
    ? ((currentMs - startMs) / timeRange) * 100
    : 0;

  // Combined state for the timeline marks and "now" position. Computed once
  // per frames/range/interval change - not on every animation render.
  const { nowMs, nowPercent, timeMarks, markLabels } =
    useMemo((): MarksInfo => {
      const now = Date.now();

      if (!hasFrames || frames.length < 2) {
        return { nowMs: now, nowPercent: null, timeMarks: [], markLabels: [] };
      }

      const percent =
        now <= startMs || now >= endMs
          ? null
          : ((now - startMs) / timeRange) * 100;

      const intervalMs = markIntervalMinutes * MINUTE_MS;
      const marks = generateTimeMarks(startMs, endMs, now, intervalMs);

      // Marks are exact multiples of intervalMs from now
      const labelIntervalMs = (markLabelIntervalMinutes ?? 0) * MINUTE_MS;
      const labeledMarks =
        labelIntervalMs > 0
          ? marks
              .filter(({ value }) => (now - value) % labelIntervalMs === 0)
              .map(({ value }) => ({
                percent: ((value - startMs) / timeRange) * 100,
                hours: Math.round((value - now) / HOUR_MS),
              }))
          : [];

      return {
        nowMs: now,
        nowPercent: percent,
        timeMarks: marks,
        markLabels: labeledMarks,
      };
    }, [
      hasFrames,
      frames.length,
      startMs,
      endMs,
      timeRange,
      markIntervalMinutes,
      markLabelIntervalMinutes,
    ]);

  return (
    <Stack
      direction="row"
      spacing={2}
      sx={{
        display: 'flex',
        alignItems: 'center',
        p: 2,
        overflow: 'hidden',
        pointerEvents: 'auto',
      }}
    >
      {isLoading || !hasFrames ? (
        <>
          {/* Play/Pause button placeholder */}
          <Skeleton
            variant="circular"
            width={PLAY_BUTTON_SIZE}
            height={PLAY_BUTTON_SIZE}
          />
          <Stack sx={{ flex: 1, minWidth: 0, pr: 1 }}>
            {/* Current time label placeholder */}
            <Skeleton
              variant="text"
              width={60}
              height={CURRENT_TIME_HEIGHT}
              sx={{ ml: 'auto' }}
            />
            {/* Slider track placeholder */}
            <Box
              sx={{
                height: SLIDER_HEIGHT,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Skeleton
                variant="rectangular"
                height={TRACK_HEIGHT}
                sx={{ flex: 1, borderRadius: 1 }}
              />
            </Box>
            {/* Time labels row placeholder */}
            <Stack
              direction="row"
              sx={{
                justifyContent: 'space-between',
                top: '-1px',
                position: 'relative',
              }}
            >
              <Skeleton variant="text" width={40} sx={styles.timeLabelStyles} />
              <Skeleton variant="text" width={40} sx={styles.timeLabelStyles} />
            </Stack>
          </Stack>
        </>
      ) : (
        <>
          {/* Play/Pause button - positioned to align center with slider track center */}
          <IconButton
            onClick={onPlayPause}
            aria-label={
              isPlaying ? t('map.timeline.pause') : t('map.timeline.play')
            }
            sx={{
              width: PLAY_BUTTON_SIZE,
              height: PLAY_BUTTON_SIZE,
              backgroundColor: 'text.primary',
              color: 'background.paper',
              '&:hover': { backgroundColor: 'grey.300' },
            }}
          >
            {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
          </IconButton>

          {/* Slider column */}
          <Stack sx={{ flex: 1, minWidth: 0, pr: 1 }}>
            {/* Current time follows slider thumb, clamped at edges */}
            <Box sx={{ position: 'relative', height: CURRENT_TIME_HEIGHT }}>
              <Typography
                ref={labelRef}
                variant="body2"
                fontWeight={600}
                sx={(theme) => ({
                  color: `${theme.palette.primary.main} !important`,
                  position: 'absolute',
                  top: '2px',
                  whiteSpace: 'nowrap',
                  left: `clamp(0px, calc(${currentPercent}% - ${labelOffset}px), calc(100% - ${labelWidth}px))`,
                  transition:
                    isPlaying && !isWrapping ? 'left 200ms linear' : 'none',
                })}
              >
                {formatDate(currentFrame!.timestamp, 'h:mm a')}{' '}
                <Typography component="span" variant="inherit" fontWeight={400}>
                  {formatDate(currentFrame!.timestamp, 'MMM d')}
                </Typography>
              </Typography>
            </Box>
            {/* Slider + NOW marker wrapper */}
            <Box sx={{ position: 'relative', height: SLIDER_HEIGHT }}>
              <WatchDutyTimelineSlider
                min={startMs}
                max={endMs}
                value={currentMs}
                marks={timeMarks}
                onChange={(_, value: number | number[]): void => {
                  const timeMs = typeof value === 'number' ? value : value[0];
                  onFrameChange(findClosestFrameIndex(frames, timeMs));
                }}
                sx={
                  isWrapping
                    ? {
                        '& .MuiSlider-thumb': { transition: 'none' },
                        '& .MuiSlider-track': { transition: 'none' },
                      }
                    : undefined
                }
              />
              {nowPercent !== null && (
                <SliderNowMarker
                  percent={nowPercent}
                  isPastNow={currentMs >= nowMs}
                />
              )}
            </Box>
            {/* Time labels */}
            <Stack
              direction="row"
              sx={{
                justifyContent: 'space-between',
                top: '-1px',
                position: 'relative',
              }}
            >
              <Typography sx={styles.emphasizedTimeLabelStyles}>
                {labels.start}
              </Typography>
              {markLabels.map(({ percent, hours }) => (
                <Typography
                  key={hours}
                  sx={{
                    ...styles.timeLabelStyles,
                    position: 'absolute',
                    left: `${percent}%`,
                    transform: 'translateX(-50%)',
                  }}
                >
                  {t('map.timeline.markLabel', { hours })}
                </Typography>
              ))}
              <Typography
                sx={
                  emphasizeEndLabel
                    ? {
                        ...styles.emphasizedTimeLabelStyles,
                        textTransform: 'uppercase',
                      }
                    : styles.timeLabelStyles
                }
              >
                {labels.end}
              </Typography>
            </Stack>
          </Stack>
        </>
      )}
    </Stack>
  );
};
